/**
 * The stateful half of Skip this round (ISSUE-1044, SUR-R87): the confirm
 * dialog, the skip and undo requests, the refusal note, and the board
 * refetch after any refusal. Every decision about what to show comes from
 * survivorSkipModel.ts, which reads only server-resolved board fields.
 */
import {
  usePickSkipRound,
  usePickUndoSkip,
} from "@sportsmet/contest-sdk/private";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import type { typeBoardData } from "../pick/pickBoardModel";
import { survivorSkipConfirmBody } from "./survivorGameCopy";
import {
  classifySkipRoundResult,
  classifyUndoSkipResult,
  resolveSkipRowView,
  type typeSkipOutcome,
  type typeSkipRowView,
} from "./survivorSkipModel";
import type { typeSurvivorGameView } from "./useSurvivorGameData";

export type typeSurvivorSkip = {
  row: typeSkipRowView | null;
  note: string | null;
  isDialogOpen: boolean;
  dialogBody: string;
  isPending: boolean;
  openDialog: () => void;
  cancelDialog: () => void;
  confirmSkip: () => void;
  undoSkip: () => void;
};

const BOARD_QUERY_KEY = ["post:round:roundGetBoard", "svc:contest-api"];

export function useSurvivorSkip(input: {
  poolId: string;
  contestId: string;
  roundId: string;
  entryId: string;
  boardData: typeBoardData | null;
  view: typeSurvivorGameView;
  stagedPickCount: number;
}): typeSurvivorSkip {
  const queryClient = useQueryClient();
  const skipRound = usePickSkipRound();
  const undoSkipMutation = usePickUndoSkip();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const requestIdRef = useRef<string>(crypto.randomUUID());

  // A switch to another entry or round never carries a note or an open dialog.
  useEffect(() => {
    setNote(null);
    setIsDialogOpen(false);
  }, [input.entryId, input.roundId]);

  const row = resolveSkipRowView({
    view: input.view,
    boardData: input.boardData,
    stagedPickCount: input.stagedPickCount,
  });
  const dialogBody = survivorSkipConfirmBody(
    input.boardData?.skipsRemaining ?? null,
  );
  const isPending = skipRound.isPending || undoSkipMutation.isPending;

  function refetchBoard(): void {
    void queryClient.invalidateQueries({ queryKey: BOARD_QUERY_KEY });
  }

  function applyOutcome(outcome: typeSkipOutcome, result: unknown): void {
    if (outcome.kind === "ok") {
      // The generated hook's own invalidation refetches the board, which
      // turns the view into skipped (after a skip) or open (after an undo).
      setNote(null);
      return;
    }
    if (outcome.kind === "inline") {
      setNote(outcome.note);
      refetchBoard();
      return;
    }
    setNote(null);
    showApiError(result);
    refetchBoard();
  }

  function openDialog(): void {
    // A new id per opening; a double tap on confirm reuses it, which the
    // server's runIdempotent collapses into one skip.
    requestIdRef.current = crypto.randomUUID();
    setNote(null);
    setIsDialogOpen(true);
  }

  function cancelDialog(): void {
    setIsDialogOpen(false);
  }

  async function confirmSkipAsync(): Promise<void> {
    setIsDialogOpen(false);
    try {
      const result = await skipRound.mutateAsync({
        poolId: input.poolId,
        contestId: input.contestId,
        roundId: input.roundId,
        entryId: input.entryId,
        requestId: requestIdRef.current,
      });
      applyOutcome(classifySkipRoundResult(result), result);
    } catch (error) {
      applyOutcome({ kind: "modal", code: "INTERNAL_ERROR" }, error);
    }
  }

  async function undoSkipAsync(): Promise<void> {
    setNote(null);
    try {
      const result = await undoSkipMutation.mutateAsync({
        poolId: input.poolId,
        roundId: input.roundId,
        entryId: input.entryId,
      });
      applyOutcome(classifyUndoSkipResult(result), result);
    } catch (error) {
      applyOutcome({ kind: "modal", code: "INTERNAL_ERROR" }, error);
    }
  }

  return {
    row,
    note,
    isDialogOpen,
    dialogBody,
    isPending,
    openDialog,
    cancelDialog,
    confirmSkip: () => {
      void confirmSkipAsync();
    },
    undoSkip: () => {
      void undoSkipAsync();
    },
  };
}
